import {Component, ElementRef, HostListener, Input, OnInit, QueryList, ViewChildren, ViewEncapsulation} from '@angular/core';
import {ToolbarFiltroStore} from './toolbar-filtro.store';
import {ToolbarFiltroItemComponent} from '../toolbar-filtro-item/toolbar-filtro-item.component';
import {Filtro} from '~model/filtro/filtros/filtro';
import {RotaVo} from '~model/vo/rota-vo';
import {Map} from 'immutable';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '~core/utils/config.service';
import { FormGroup } from '@angular/forms';
import { AppState } from 'src/app/app.store';
import { IconRegistryService } from '~core/utils/icon-registry.service';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-toolbar-filtro',
  templateUrl: './toolbar-filtro.component.html',
  styleUrls: ['./toolbar-filtro.component.scss'],
  providers: [ToolbarFiltroStore],
  encapsulation: ViewEncapsulation.None,
})
export class ToolbarFiltroComponent implements OnInit {

  @Input() filtros!: Observable<string[] | null>;
  @Input() state$!: Observable<AppState>;
  @Input() statusFilterValue!: string;
  @Input() searchFormGroup!: FormGroup; // Recebe o FormGroup do GridComponent

  mostrarStatus$!: Observable<boolean>;
  temFiltros$!: Observable<boolean>;
  appliedCount$!: Observable<number>;

  itens: BehaviorSubject<string[][]> = new BehaviorSubject<string[][]>([]);
  itemNome$!: Observable<string[] | undefined>;
  itemInicio$!: Observable<string[] | undefined>;
  itemFim$!: Observable<string[] | undefined>;

  @ViewChildren(ToolbarFiltroItemComponent) filtroItems!: QueryList<ToolbarFiltroItemComponent>;

  langSubscription!: Subscription;

  selectedStatus: string = 'Todos'; // Valor padrão (já aplicado)

  isOpen: boolean = false;

  // Valor de status selecionado no painel (rascunho); aplicado apenas ao clicar em "Aplicar"
  private readonly draftStatusSubject = new BehaviorSubject<string>('Todos');

  get draftStatus(): string {
    return this.draftStatusSubject.value;
  }

  set draftStatus(value: string) {
    this.draftStatusSubject.next(value ?? 'Todos');
  }

  // Filtros escolhidos no painel (rascunho), aplicados apenas ao clicar em "Aplicar"
  private readonly pendingFiltrosSubject = new BehaviorSubject<Map<string, Filtro<RotaVo>>>(Map<string, Filtro<RotaVo>>());

  private get pendingFiltros(): Map<string, Filtro<RotaVo>> {
    return this.pendingFiltrosSubject.value;
  }

  private set pendingFiltros(value: Map<string, Filtro<RotaVo>>) {
    this.pendingFiltrosSubject.next(value);
  }

  status: string[] = [
    'Todos',
    'Não iniciadas',
    'Em andamento',
    'Finalizadas',
    'Canceladas',
  ];

  constructor(
    private readonly state: ToolbarFiltroStore,
    private readonly translate: TranslateService,
    private readonly configService: ConfigService,
    private readonly iconsService: IconRegistryService,
    private readonly store: Store,
    private readonly elementRef: ElementRef
  ) {
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Painéis do Angular Material (mat-select, mat-menu, etc.) são renderizados fora
    // deste componente, dentro do cdk-overlay-container — não devem fechar o painel de filtros.
    if (target?.closest?.('.cdk-overlay-container')) {
      return;
    }

    if (this.isOpen && !this.elementRef.nativeElement.contains(target)) {
      this.isOpen = false;
    }
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  ngOnInit(): void {
    this.iconsService.setUp();
    this.mostrarStatus$ = this.filtros.pipe(map(filtros => filtros !== null && (!filtros.length || filtros.includes('status'))));
    this.temFiltros$ = combineLatest([this.itens, this.mostrarStatus$]).pipe(
      map(([itens, mostrarStatus]) => itens.length > 0 || mostrarStatus)
    );
    this.itemNome$ = this.itens.pipe(map(itens => itens.find(item => item[1] === 'nome')));
    this.itemInicio$ = this.itens.pipe(map(itens => itens.find(item => item[1] === 'horarioInicio')));
    this.itemFim$ = this.itens.pipe(map(itens => itens.find(item => item[1] === 'horarioFim')));

    // Inicializar o rascunho com os filtros já aplicados
    this.pendingFiltros = this.state.getState().filtros;

    // Inicializar selectedStatus/draftStatus com o valor atual do form
    this.selectedStatus = this.searchFormGroup.get('statusFilterForm')?.value ?? 'Todos';
    this.draftStatus = this.selectedStatus;

    // Atualiza o valor selecionado sempre que o statusFilterForm mudar (ex.: aplicado por outra origem)
    this.searchFormGroup.get('statusFilterForm')?.valueChanges.subscribe(value => {
      this.selectedStatus = value ?? 'Todos';
    });

    // Quantidade de filtros (Busca/Início/Fim/Status) selecionados no painel neste momento
    // (reflete o rascunho em edição, para o número já bater assim que o usuário escolhe algo)
    this.appliedCount$ = combineLatest([
      this.pendingFiltrosSubject,
      this.draftStatusSubject,
    ]).pipe(
      map(([pendingFiltros, status]) => {
        let count = 0;
        pendingFiltros.forEach(filtro => {
          if (filtro.getComparadores().length > 0) {
            count++;
          }
        });
        if (status && status !== 'Todos') {
          count++;
        }
        return count;
      })
    );

    combineLatest([
      this.filtros,
      this.configService.getLangChangeObserver(),
    ]).pipe(
      switchMap(([filtros, _]) =>
        this.translate.get('FILTER.PROPERTIES').pipe(
          map(propertiesStrings => ({ filtros, propertiesStrings }))
        )
      )
    ).subscribe(({ filtros, propertiesStrings }) => {
      const filterObject = {
        nome: propertiesStrings.NAME,
        horarioInicio: propertiesStrings.START,
        horarioFim: propertiesStrings.END,
      };

      if (filtros === null) {
        this.itens.next([]);
        return;
      }

      this.itens.next(Map(filterObject)
        .filter((k, v) => {
          return !filtros.length || filtros.includes(v);
        })
        .map((k, v) => {
          return [k, v];
        }).valueSeq().toArray());
    });
  }

  // Aplicação imediata: cada mudança nos campos (chip adicionado/removido na busca,
  // horário de Início/Fim escolhido) já filtra a lista, sem botão "Aplicar"
  objHandler($event: Filtro<RotaVo>): void {
    this.pendingFiltros = this.pendingFiltros.set($event.getFiltroNome(), $event);
    this.state.applyFiltros(this.pendingFiltros);
  }

  // Status também aplica assim que muda no select
  applyStatus(value: string): void {
    this.draftStatus = value ?? 'Todos';
    this.selectedStatus = this.draftStatus;
    this.searchFormGroup.get('statusFilterForm')?.setValue(this.draftStatus);
  }

  limpar(): void {
    // "Limpar" age imediatamente: remove todos os filtros já aplicados (Busca/Início/Fim/Status)
    // e atualiza a lista na hora, sem esperar o "Aplicar"
    this.pendingFiltros = Map<string, Filtro<RotaVo>>();
    this.draftStatus = 'Todos';
    this.selectedStatus = 'Todos';

    this.state.clearAllFilters();
    this.searchFormGroup.get('statusFilterForm')?.setValue('Todos');

    // Reseta o valor visual de cada campo (Busca/Início/Fim) sem destruir/recriar os componentes,
    // evitando o "piscar" do painel que ocorria ao esvaziar e repopular a lista de itens.
    this.filtroItems?.forEach(item => item.reset());
  }

  clearStatusFilter(): void {
    this.applyStatus('Todos');
  }
}
